<template>
    <div>
        <vx-auto-suggest-modified
            ref="searchInput"
            v-bind="attrs"
            background-overlay
            hide-group-title
            class="vx-auto-suggest-modified w-full"
            input-classes="w-full vs-input-no-shadow-focus"
            icon="SearchIcon"
            prevent-filtering
            remove-validation-padding
            :value="searchText"
            :select-like="false"
            :autofocus="autofocus || showFullSearch"
            :data="predictions"
            :selected.sync="selected"
            :show-on-focus="true"
            @input="searchChanged"
            @blur="(e) => $emit('blur', e)"
            @focus="(e) => $emit('focus', e)"
            @close-searchbar="closeSearch"
        >
            <template v-slot:places="{ suggestion }">
                <slot name="places" :place="suggestion">
                    <div class="flex items-center leading-none py-1">
                        <vx-basic-icon
                            use-mdi
                            :icon="getIcon(suggestion.types)"
                            size="6"
                            class="list-icon mr-4"
                        />
                        <div>
                            <h5 class="">{{ suggestion.structured_formatting.main_text }}</h5>
                            <span class="">{{
                                suggestion.structured_formatting.secondary_text
                            }}</span>
                        </div>
                    </div>
                </slot>
            </template>

            <!-- Loading Slot -->
            <template v-slot:loader>
                <span
                    v-if="loaderState == 'listening-more'"
                    class="inline-flex items-center text-warning"
                >
                    <vx-basic-icon icon="AlertTriangleIcon" class="mr-3" />
                    Minimum {{ minSearchableCharacters }} Characters
                </span>
                <vx-spinner
                    v-else-if="loaderState != 'dormant'"
                    scale="0.6"
                    :type="loaderState == 'listening' ? 'sound' : 'default'"
                />
                <div v-else>
                    <!-- This is only here to prevent the default -->
                </div>
            </template>

            <!-- No Items Slot -->
            <template v-slot:noResult>
                <slot name="noResult">
                    <div class="flex items-center">
                        <feather-icon icon="InfoIcon" svgClasses="h-6 w-6" class="mr-4" />
                        <span v-if="waitingForMoreText">Keep typing...</span>
                        <span v-else>No matching places</span>
                    </div>
                </slot>
            </template>
        </vx-auto-suggest-modified>
        <div class="places_target" ref="places_target"></div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    name: 'vx-google-place-search',
    props: {
        value: {
            type: Object
        },
        debounce: {
            type: Number,
            default: 100
        },
        autofocus: {
            type: Boolean,
            default: false
        },
        australiaOnly: {
            type: Boolean,
            default: false
        },
        selectLike: {
            type: Boolean,
            default: true
        },
        showOnFocus: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
            validator: (v) => ['address', 'geocode', 'establishment'].contains(v)
        },
        displayKey: {
            type: [String, Function]
        },
        getContactInfo: {
            type: Boolean,
            default: false
        },
        getAtmosphereInfo: {
            type: Boolean,
            default: false
        },
        apiKey: {
            type: String,
            // required: true,
            default: 'AIzaSyBpUOiyI6B6jMi7VchRiey-e_29wKFKSkA'
        },
        minSearchableCharacters: {
            type: Number,
            default: 4
        }
    },
    data() {
        return {
            searchText: '',
            showFullSearch: false,
            typingTimeout: null,
            sessionToken: '',
            prediction_results: [],
            loading_predictions: false,
            // Part of base 1.7c cost
            basic_fields: [
                'address_component',
                'adr_address',
                'business_status',
                'formatted_address',
                'geometry',
                'icon',
                'name',
                'photo',
                'place_id',
                'plus_code',
                'type',
                'url',
                'vicinity'
            ],
            // Costs additional 0.3c per session
            contact_fields: [
                'formatted_phone_number',
                'international_phone_number',
                'opening_hours',
                'website'
            ],
            // Costs additional 0.5c per session
            atmos_fields: ['price_level', 'rating', 'review', 'user_ratings_total'],
            newAddress: {
                streetAddress1: null,
                streetAddress2: null,
                suburb: null,
                postcode: null,
                country: null,
                state: null
            }
        }
    },
    computed: {
        ...mapGetters({
            getStateByCode: 'crm/getStateByCode'
        }),
        waitingForMoreText() {
            return this.searchText.length < this.minSearchableCharacters
        },
        predictions() {
            return {
                places: {
                    loading: this.loading_predictions,
                    getSearchText: this.getSearchText,
                    id: 'place_id',
                    data: this.prediction_results
                }
            }
        },
        fieldList() {
            return [
                ...this.basic_fields,
                ...(this.getContactInfo ? this.contact_fields : []),
                ...(this.getAtmosphereInfo ? this.atmos_fields : [])
            ]
        },
        attrs() {
            return {
                ...this.$attrs,
                selected: null
            }
        },
        selected: {
            get() {
                return this.value
            },
            set(val) {
                this.onSelected(val)
            }
        },
        loaderState() {
            if (this.searchText == null || this.searchText == '') {
                return 'dormant'
            }

            if (this.typingTimeout != null) {
                return 'listening'
            }

            if (this.waitingForMoreText) {
                return 'listening-more'
            }
            return 'loading'
        }
    },
    methods: {
        getIcon(types) {
            if (types.contains('store')) {
                return 'store'
            }
            if (types.contains('church')) {
                return 'church'
            }
            if (types.contains('store')) {
                return 'store'
            }
            if (types.contains('school')) {
                return 'school'
            }
            if (types.contains('establishment')) {
                return 'business'
            }
            if (types.contains('premise')) {
                return 'house'
            }
            return 'pin_drop'
        },
        startSession() {
            if (!this.sessionToken) {
                this.sessionToken = new window.google.maps.places.AutocompleteSessionToken()
            }
            if (!this.autocompleteService) {
                this.autocompleteService = new window.google.maps.places.AutocompleteService()
            }
        },
        endSession() {
            this.sessionToken = ''
        },
        getSearchText(item) {
            if (item) {
                return item.description || item.formatted_address
            }
            return ''
        },
        onSelected({ item }) {
            if (this.value && this.value.place_id == item.place_id) {
                return
            }
            try {
                this.$emit('retrieving')
                this.startSession()
                var target = this.$refs.places_target
                const placeService = new window.google.maps.places.PlacesService(target)

                var payload = {
                    placeId: item.place_id,
                    sessionToken: this.sessionToken,
                    fields: this.fieldList
                }
                placeService.getDetails(payload, (place) => {
                    this.newAddress = this.parseAddressInfo(place)
                    this.$emit('input', this.newAddress)
                })
                this.endSession()
            } catch (err) {
                this.$emit('error', item)
                this.notifyError(
                    'Missing Record',
                    `Could not find details for ${this.searchText}. ${err}`,
                    8000
                )
            }
            this.closeSearch()
        },
        closeSearch() {
            this.showFullSearch = false
        },
        searchChanged(term) {
            // if (!this.term || this.term.length < this.minSearchableCharacters) {
            //     this.loading_predictions = false
            //     this.prediction_results = []
            //     return
            // }
            this.loading_predictions = true
            this.searchText = term

            if (this.waitingForMoreText) {
                return
            }
            if (this.typingTimeout) {
                clearTimeout(this.typingTimeout)
                this.typingTimeout = null
            }

            this.typingTimeout = setTimeout(
                function () {
                    this.typingTimeout = null
                    try {
                        if (term) {
                            this.fetchPredictions(term)
                            return
                        }
                        this.prediction_results = []
                    } finally {
                        this.loading_predictions = false
                    }
                }.bind(this),
                this.listenFor
            )
        },
        async fetchPredictions(term) {
            if (this.waitingForMoreText || (this.value && this.value.description == term)) {
                return
            }

            try {
                this.startSession()
                this.loading_predictions = true
                var packet = {
                    input: term,
                    sessionToken: this.sessionToken
                }
                if (this.australiaOnly) {
                    packet.componentRestrictions = {
                        country: 'au'
                    }
                }

                if (this.type) {
                    packet.type = this.type
                }
                this.prediction_results = await this.fetchPredictionsAsync(packet)
            } catch (err) {
                console.error(err)
            } finally {
                this.loading_predictions = false
            }
        },
        fetchPredictionsAsync(packet) {
            return new Promise((resolve, reject) => {
                this.autocompleteService.getPlacePredictions(packet, (predictions, status) => {
                    if (status != 'OK') {
                        reject(status)
                    }
                    resolve(predictions || [])
                })
            })
        },
        focus() {
            this.$refs.searchInput.focus()
        },
        findAddressPart(googleAddress, type, short) {
            if (!googleAddress) {
                return ''
            }
            var types = [type]
            var component = googleAddress.address_components.first((p) =>
                p.types.containsAll(types)
            )
            if (!component) {
                return ''
            }
            if (short) {
                return component['short_name']
            }
            return component['long_name']
        },
        parseAddressInfo(address) {
            return {
                ...this.parseStreetAddressLines(address),
                suburb: this.findAddressPart(address, 'locality'),
                postcode: this.findAddressPart(address, 'postal_code', true),
                country: this.findAddressPart(address, 'country'),
                state: this.findAddressPart(address, 'administrative_area_level_1', true)
            }
        },
        parseStreetAddressLines(address) {
            var streetNumber = this.findAddressPart(address, 'street_number')
            var route = this.findAddressPart(address, 'route')
            var streetAddress = `${streetNumber} ${route}`.trim()
            var subPremise = this.parseSubPremise(address)

            if (subPremise) {
                return {
                    streetAddress1: subPremise,
                    streetAddress2: streetAddress
                }
            }
            return {
                streetAddress1: streetAddress,
                streetAddress2: null
            }
        },
        parseSubPremise(address) {
            var subPremise = this.findAddressPart(address, 'subpremise')
            return subPremise || this.findAddressPart(address, 'premise')
        }
    },
    mounted() {
        if (window.google && window.google.maps && window.google.maps.places) {
            return
        }
        let recaptchaScript = document.createElement('script')
        recaptchaScript.setAttribute(
            'src',
            `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`
        )
        document.head.appendChild(recaptchaScript)
    }
}
</script>

<style scoped>
.vx-auto-suggest-modified >>> .list-icon {
    transition: 0.2s all;
}

.vx-auto-suggest-modified >>> .vx-auto-suggest__current-selected .list-icon {
    margin-right: 1.25rem !important;
    color: rgba(var(--vs-primary), 1);
}

::v-deep .vs-con-input-label {
    width: 100% !important;
}
</style>
